import { useState } from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';

import { PicturesUpload } from '@common/ui/PicturesUpload';
import { Heading } from '@common/ui/typography/Heading';
import { Paragraph } from '@common/ui/typography/Paragraph';

import {
  UNKNOWN_PHOTO_ERROR_MESSAGE,
  useUploader,
} from '@entities/profile/hooks/useUploader';

import { analytics } from '@services/AnalyticsService';

import type { SignUpFormData } from '../../SignUp';

interface PicturesProps {
  control: Control<SignUpFormData>;
  errors: FieldErrors<SignUpFormData>;
  /** The light (matchmaker) sign-up takes exactly one photo. */
  isSinglePhoto?: boolean;
}

export const Pictures = ({
  control,
  errors,
  isSinglePhoto = false,
}: PicturesProps) => {
  const [error, setError] = useState<string>();

  const uploader = useUploader({
    onUnknownError: () => {
      console.log('Unknown error in Pictures component');
      analytics.trackSignUpStep('upload_failed', { reason: 'unknown' });
      setError(UNKNOWN_PHOTO_ERROR_MESSAGE);
    },
    onFileTooLarge: ({ message }) => {
      console.log('File too large in Pictures component:', message);
      analytics.trackSignUpStep('upload_failed', { reason: 'too_large' });
      setError(message);
    },
  });

  return (
    <>
      <Heading level={2} style={{ textAlign: 'center' }}>
        {isSinglePhoto ? 'Ваше фото' : 'Ваши фотографии'}
      </Heading>
      <Paragraph style={{ textAlign: 'center', marginTop: -20 }}>
        {isSinglePhoto ? (
          <span data-test-id="pictures-single-photo-hint">
            Одно фото — для проверки анкеты модератором
          </span>
        ) : (
          'Добавьте от 1 до 3 фотографий. Нажмите на фотографию, чтобы сделать ее главной'
        )}
      </Paragraph>

      <Controller
        control={control}
        name="pictures"
        render={({ field }) => (
          <PicturesUpload
            maxNumber={isSinglePhoto ? 1 : 3}
            error={errors.pictures?.message?.toString() || error}
            onChange={(v) => {
              field.onChange(v);
              if (v && v.length > 0) {
                setError(undefined);
              }
            }}
            upload={uploader}
          />
        )}
      />

      <Paragraph variant="secondary" style={{ textAlign: 'center' }}>
        (Загружайте только реальные)
      </Paragraph>
    </>
  );
};
